import React, { useEffect, useState } from "react";
import APIClient from "web_modules/APIClient";
import BaseTable, { EditingState } from "web_modules/shared/BaseTable";
import Validations from "web_modules/shared/FormHelpers/validation";
import { FeePaidByType } from "web_modules/forms/data/feePaidByOptions";
import { formatCurrency } from "@eave/shared/utils/currency";
import {
  AppShape,
  DisclosureFee,
  DisclosureShape,
  DisclosureStage,
  DisclosureType,
} from "@eave/shared/types/api";
import { Locked32, Unlocked32 } from "@carbon/icons-react";
import { Button, InlineLoading, InlineNotification, Loading, Modal } from "carbon-components-react";
import { sortBy } from "lodash";
import { LoadingState } from "underwriter_dashboard/shared/types";
import CICButton from "underwriter_dashboard/hurdle_editors/Fees/CICButton";
import CICSelector from "underwriter_dashboard/hurdle_editors/Fees/CICSelector";

import FeesModalForm from "./FeesModalForm";
import {
  feesByDisclosureSection,
  populateFeesFromClosingCorp,
  showPopulateClosingCorpFeesButton,
} from "./helpers";
import { feesTableHeadings, feesTableSectionLabels } from "./shared";

const feeStyles = require("./styles.module.scss");

interface Props {
  app: AppShape;
  fees: DisclosureFee[];
  loadFees: Function;
  selectedDisclosure: DisclosureType;
  updateSelectedDisclosure: Function;

  formState: DisclosureFee;
  updateFormState: Function;
  resetForm: Function;
  disclosures: {
    [index: number]: DisclosureShape;
    length: number;
    slice: Function;
    map: Function;
    some: Function;
  };
  tridCure: number;
  lenderCreditAmount: number;
  recipientOptions: any;
  prepaids: DisclosureFee[];
  escrows: DisclosureFee[];
}

export interface ComputedValuesForFeeType {
  tolerance: string;
  included_in_apr: boolean;
  le_section: string;
  cd_section: string;
}

const placeholder = "-";

const FeeTable: React.FC<Props> = ({
  app,
  tridCure,
  fees,
  loadFees,
  selectedDisclosure,
  updateSelectedDisclosure,
  disclosures,

  formState,
  updateFormState,
  resetForm,
  lenderCreditAmount,
  recipientOptions,
  prepaids,
  escrows,
}: Props) => {
  const findIfLESent = () => {
    return disclosures.some((disclosure: DisclosureShape) => {
      return disclosure.esign && disclosure.stage === DisclosureStage.LoanEstimate;
    });
  };
  // If an LE HAS been sent, SHOW the cicSelector
  // If an LE HAS NOT been sent, HIDE the cicSelector
  const cicSelectorVisible = selectedDisclosure === DisclosureType.LoanEstimate && findIfLESent();

  // If an CD stage is PreliminaryClosingDisclosure or Redisclosure AND is e-sign, SHOW the CIC selector
  const findIfCDSent = () => {
    return disclosures.some((disclosure: DisclosureShape) => {
      const isRedisclosure = disclosure.stage === DisclosureStage.Redisclosure;
      const isPreliminaryDisclosure =
        disclosure.stage === DisclosureStage.PreliminaryClosingDisclosure;
      return disclosure.esign && (isRedisclosure || isPreliminaryDisclosure);
    });
  };

  const cicSelectorCDVisible =
    selectedDisclosure === DisclosureType.ClosingDisclosure && findIfCDSent();

  const cicFeeSection = app.undisclosed_change_in_circumstance?.fee_section;
  const isCreatedByLoanEstimateSection =
    cicFeeSection !== undefined &&
    cicFeeSection !== "LE" &&
    selectedDisclosure === DisclosureType.LoanEstimate;

  enum lockedReasonOptions {
    NoCIC = "NoCIC",
  }

  const lockedReason = (() => {
    if (selectedDisclosure === DisclosureType.ClosingDisclosure) {
      return "";
    }

    if (findIfLESent() && !app.undisclosed_change_in_circumstance) {
      return lockedReasonOptions.NoCIC;
    }
    return "";
  })();

  const locked = lockedReason !== "";

  const howToUnlock = {
    [lockedReasonOptions.NoCIC]: "Select a CIC to unlock",
  };

  // Groups fees by section label
  const [groupedFees, updateGroupedFees] = useState(null);
  const [selectedCIC, updateSelectedCIC] = useState(app.undisclosed_change_in_circumstance?.code);

  // UI Hooks
  const [deleteModalIsOpen, updateDeleteModalIsOpen] = useState(false);
  const [feesModalIsOpen, updateFeesModalIsOpen] = useState(false);
  const [populateActualsModalIsOpen, updatePopulateActualsModalIsOpen] = useState(false);
  const [editingMode, updateEditingMode] = useState(EditingState.CREATE);
  const [loadingState, updateLoadingState] = useState(LoadingState.INACTIVE);
  const [CICLoading, updateCICLoading] = useState(false);
  const [showCICDeletionModal, updateShowCICDeletionModal] = useState(false);
  const [errorMessage, updateErrorMessage] = useState("");

  // When user selects "Other" for CIC, show this textbox
  const [showOtherCICInput, updateShowOtherCICInput] = useState(false);
  const [otherCICDescription, updateOtherCICDescription] = useState("");

  // This data is computed on back-end
  const [computedFeeData, updateComputedFeeData] = useState({} as ComputedValuesForFeeType);

  const { IsValid } = Validations;

  // The name of the object key depending on whether or not it's an LE or CD
  const totalKey =
    selectedDisclosure === DisclosureType.LoanEstimate ? "estimated_amount" : "actual_amount";

  const readOnlySections = ["F", "G"];

  // Rather than multiple filters, do a single groupBy(), resulting in an object that we can iterate over
  useEffect(() => {
    APIClient.get(`/uw/${app.id}/fees_computed_values`).then(
      ({ fee_types_with_computed_values }) => {
        updateComputedFeeData(fee_types_with_computed_values);
      },
    );
  }, [app.id]);

  useEffect(() => {
    const allFees = [...escrows, ...prepaids, ...fees].filter(fee => fee);

    updateGroupedFees(feesByDisclosureSection(allFees, selectedDisclosure));
  }, [fees, selectedDisclosure, prepaids, escrows]);

  const renderEmptyFeesTable = ({ letter, label }) => {
    return (
      <BaseTable
        key={label}
        title={`${letter}. ${label} - $0`}
        collapseOverride // collapse by default, since this is for empty tables!
        style={{ marginBottom: "0.75rem", width: "100%" }}
        headerData={feesTableHeadings(selectedDisclosure)}
        rowData={[]}
        disableAllControls
      />
    );
  };

  const toleranceToString = tolerance => {
    switch (tolerance) {
      case "ZeroPercent":
        return "0%";
      case "TenPercent":
        return "10%";
      case "Unlimited":
        return <span style={{ fontSize: "25px" }}>∞</span>;
      case "Unknown":
        return "-";
      default:
        return tolerance;
    }
  };

  // Returns a row representing a total of some set of fees
  const renderTotalRow = ({ letter, label }) => {
    const sectionKey =
      selectedDisclosure === DisclosureType.LoanEstimate ? "le_section" : "cd_section";

    // Maps the total row's section to an array of the sections whose fees it should include in its total
    const totalsMap = {
      D: ["A", "B", "C"],
      I: ["E", "F", "G", "H"],
      J: ["A", "B", "C", "E", "F", "G", "H"],
    };

    // Filter the fees to only those that are included in the current "total row"'s section
    let filteredFees = [...fees, ...prepaids, ...escrows].filter(f =>
      totalsMap[letter].includes(f[sectionKey]),
    );

    // For CDs, only the borrower-paid fees should be included in the totals
    if (selectedDisclosure === DisclosureType.ClosingDisclosure) {
      filteredFees = filteredFees.filter(f => f.paid_by_type === FeePaidByType.Buyer);
    }

    // Flattens the fees to a single total
    let total = filteredFees
      .map(f => Number(f[totalKey]) || 0)
      .reduce((acc, curr) => acc + curr, 0);

    total = letter === "J" ? total - tridCure - lenderCreditAmount : total;

    return (
      <div className={feeStyles.totalRow}>
        <p>
          {letter}. {label}
        </p>
        <p> {formatCurrency(total, 2, 2)}</p>
      </div>
    );
  };

  const renderFeesTable = ({ letter, label }, sectionFees: DisclosureFee[]) => {
    // Alphabetize the fees in the table by description
    const sortedFees = sortBy(sectionFees, f => f.description);
    const feeTotalForTable =
      selectedDisclosure === DisclosureType.LoanEstimate
        ? sortedFees
            .map(f => parseFloat(f[totalKey]) || 0)
            .reduce((total, current: number) => total + current, 0)
        : sortedFees
            .filter(f => f.paid_by_type === FeePaidByType.Buyer)
            .map(f => parseFloat(f[totalKey]) || 0)
            .reduce((total, current: number) => total + current, 0);

    return (
      <BaseTable
        key={label}
        prefix={letter}
        title={`${letter}. ${label} - ${formatCurrency(feeTotalForTable, 2, 2)}`}
        disableAllControls={locked}
        style={{ marginBottom: "0.75rem" }}
        updateFormState={updateFormState}
        updateEditingMode={updateEditingMode}
        openModal={() => updateFeesModalIsOpen(true)}
        openDeleteModal={() => updateDeleteModalIsOpen(true)}
        readOnlySections={readOnlySections}
        onBatchDelete={selectedRawRowsData => {
          updateLoadingState(LoadingState.ACTIVE);

          // The endpoint is batch-capable, so instead of doing a Promise.all(), we can just send all IDS to the back-end!
          const rowsToDelete = selectedRawRowsData.map(r => {
            return {
              id: r.id,
              deleted: true,
            };
          });

          APIClient.put(`/uw/${app.id}/fees`, {
            fees: rowsToDelete,
          })
            .then(() => {
              loadFees();
              updateFormState(null);
            })
            .catch(err => {
              updateErrorMessage(err);
            })
            .finally(() => {
              updateLoadingState(LoadingState.FINISHED);
            });
        }}
        headerData={feesTableHeadings(selectedDisclosure)}
        allowHeaderTextWrapping
        rawRowData={sortedFees}
        rowData={sortedFees.map(f => {
          return {
            ...f,
            id: `${f.id}`,
            key: `${f.id}`,
            estimated_amount:
              f.estimated_amount === null ? placeholder : formatCurrency(f.estimated_amount, 2, 2),
            actual_amount:
              f.actual_amount === null ? placeholder : formatCurrency(f.actual_amount, 2, 2),
            included_in_apr: f.included_in_apr ? "Yes" : "No",
            paid_before_closing: f.paid_before_closing ? "Yes" : "No",
            tolerance: toleranceToString(f.tolerance),
          };
        })}
        rowHeight="compact"
        disableAddNew // AddNew is handled in the top-level fee component
        disableBatchEdit
      />
    );
  };

  return (
    <>
      {/* Loading spinner for table */}
      <Loading active={loadingState === LoadingState.ACTIVE} />

      {/* Modals */}
      <Modal
        danger
        open={deleteModalIsOpen}
        modalHeading="Are you sure you'd like to delete this record?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onBlur={() => updateDeleteModalIsOpen(false)}
        onRequestClose={() => updateDeleteModalIsOpen(false)}
        onRequestSubmit={() => {
          updateLoadingState(LoadingState.ACTIVE);

          APIClient.put(`/uw/${app.id}/fees`, {
            fees: [
              {
                ...formState,
                loan_application_id: app.id,
                deleted: true,
              },
            ],
          })
            .then(() => {
              loadFees();
              resetForm();
              updateDeleteModalIsOpen(false);
            })
            .catch(err => {
              updateErrorMessage(err);
            })
            .finally(() => {
              updateLoadingState(LoadingState.FINISHED);
            });
        }}
      />

      <Modal
        danger
        open={showCICDeletionModal}
        modalHeading="Are you sure you'd like to clear the CIC?"
        primaryButtonText="Delete CIC"
        secondaryButtonText="Cancel"
        onBlur={() => updateShowCICDeletionModal(false)}
        onRequestClose={() => updateShowCICDeletionModal(false)}
        onRequestSubmit={() => {
          updateLoadingState(LoadingState.ACTIVE);

          APIClient.deleteRequest(
            `/uw/loan_applications/${app.id}/change_in_circumstance/${app.undisclosed_change_in_circumstance.id}`,
          )
            .then(() => {
              window.location.reload();
            })
            .catch(err => {
              updateLoadingState(LoadingState.FINISHED);
              updateErrorMessage(err);
            });
        }}
      />

      <Modal
        danger
        open={populateActualsModalIsOpen}
        modalHeading="Are you sure you want to copy estimated fees into actual fees?"
        primaryButtonText="Populate Actuals"
        secondaryButtonText="Cancel"
        onBlur={() => updatePopulateActualsModalIsOpen(false)}
        onRequestClose={() => updatePopulateActualsModalIsOpen(false)}
        onRequestSubmit={() => {
          updateLoadingState(LoadingState.ACTIVE);

          // any existing `actual_amount`s are not changed
          const feesWithActuals = fees
            .filter(f => f.actual_amount === null)
            .map(f => ({
              ...f,
              actual_amount: f.estimated_amount,
            }));

          APIClient.put(`/uw/${app.id}/fees`, {
            fees: feesWithActuals,
          })
            .then(() => {
              loadFees();
              resetForm();
              updatePopulateActualsModalIsOpen(false);
            })
            .catch(err => {
              updateErrorMessage(err);
            })
            .finally(() => {
              updateLoadingState(LoadingState.FINISHED);
            });
        }}
      >
        <div className={feeStyles.populateActuals}>
          <p>This will update fees that have unset actual amounts.</p>

          <p>An unset actual amount is displayed as "{placeholder}".</p>

          <p>"$0.00" counts as a set actual amount.</p>
        </div>
      </Modal>

      <Modal
        open={feesModalIsOpen}
        modalHeading={
          editingMode === EditingState.CREATE
            ? `Add ${selectedDisclosure} Fee`
            : `Update ${selectedDisclosure} Fee`
        }
        primaryButtonText={editingMode === EditingState.CREATE ? "Save" : "Update"}
        secondaryButtonText="Cancel"
        selectorPrimaryFocus="#fee-description"
        primaryButtonDisabled={
          // Each disclosure has different validation rules
          selectedDisclosure === DisclosureType.LoanEstimate
            ? !(
                formState !== null &&
                IsValid(formState.description) &&
                IsValid(formState.estimated_amount) &&
                IsValid(formState.mismo_type) &&
                IsValid(formState.paid_by_type) &&
                IsValid(formState.paid_to_type) &&
                IsValid(formState.recipient)
              )
            : !(
                formState !== null &&
                (formState.actual_amount === "" ||
                  (formState.actual_amount !== "" && IsValid(formState.actual_amount))) &&
                IsValid(formState.description) &&
                IsValid(formState.mismo_type) &&
                IsValid(formState.paid_by_type) &&
                IsValid(formState.paid_to_type) &&
                IsValid(formState.recipient)
              )
        }
        onRequestSubmit={() => {
          const formData = [{ ...formState, loan_application_id: app.id }];

          updateLoadingState(LoadingState.ACTIVE);

          // Endpoint for INSERT and UPDATE are the same
          APIClient.put(`/uw/${app.id}/fees`, {
            fees: formData,
          })
            .then(() => {
              loadFees();
              resetForm();
              updateLoadingState(LoadingState.FINISHED);
              updateFeesModalIsOpen(false);
            })
            .catch(err => {
              updateLoadingState(LoadingState.FINISHED);
              updateErrorMessage(err);
            });
        }}
        onRequestClose={() => {
          resetForm();
          updateFeesModalIsOpen(false);
        }}
        onBlur={() => {
          resetForm();
          updateFeesModalIsOpen(false);
        }}
      >
        {errorMessage.length > 0 && (
          <InlineNotification
            kind="error"
            notificationType="inline"
            role="alert"
            statusIconDescription="Error"
            subtitle={errorMessage}
            title="Error"
            onCloseButtonClick={() => {
              updateErrorMessage("");
            }}
          />
        )}
        {// need this guard, as rendering <FeesModalForm />
        // when the <Modal /> actually isn't open ruins the initial component values,
        // specifically for <ComboBox initialSelectedItem />.

        feesModalIsOpen && (
          <FeesModalForm
            computedFeeData={computedFeeData}
            disclosure={selectedDisclosure}
            editingMode={editingMode}
            formState={formState}
            updateFormState={updateFormState}
            updateSelectedDisclosure={updateSelectedDisclosure}
            recipientOptions={recipientOptions}
          />
        )}
      </Modal>

      <div className={feeStyles.feesContainer}>
        <div className={feeStyles.feesHeading}>
          <div style={{ display: "flex" }}>
            <h2 style={{ marginRight: "10px" }}>Fees</h2>
          </div>
          <span className={feeStyles.globalButtons}>
            <Button
              disabled={locked || isCreatedByLoanEstimateSection}
              kind="primary"
              onClick={() => {
                updateEditingMode(EditingState.CREATE);
                updateFeesModalIsOpen(true);
              }}
            >
              Add New Fee
            </Button>
            {showPopulateClosingCorpFeesButton(app) && (
              <Button
                kind="secondary"
                onClick={() => {
                  updateLoadingState(LoadingState.ACTIVE);

                  populateFeesFromClosingCorp(app.id)
                    .then(() => {
                      window.location.reload();
                    })
                    .catch(err => {
                      // TODO this, and other error messages, actually don't display on the page,
                      // since the <InlineNotification /> that displays the error is within feesModalIsOpen :)

                      const error =
                        (err && err.meta && err.meta.error_detail) || "There was an error";
                      updateErrorMessage(error);

                      updateLoadingState(LoadingState.FINISHED);
                    });
                }}
              >
                Populate fees from ClosingCorp
              </Button>
            )}
            <Button
              kind="secondary"
              onClick={() => {
                updatePopulateActualsModalIsOpen(true);
              }}
            >
              Copy Estimates Into Actuals
            </Button>
          </span>
        </div>

        <div className={feeStyles.lock}>
          <div className={feeStyles.lockIcon}>
            {locked ? (
              <Locked32 style={{ transform: "scale(2)" }} />
            ) : (
              <Unlocked32 style={{ transform: "scale(2)" }} />
            )}
          </div>
          <div className={feeStyles.lockLabel}>
            {locked ? (
              <>
                <p>Fees editing is disabled.</p>
                <span>{howToUnlock[lockedReason]}</span>
              </>
            ) : (
              <div style={{ display: "flex", minWidth: "125px" }}>
                <p>Fees editing is enabled.</p>
                {app.undisclosed_change_in_circumstance && (
                  <span>
                    CIC: {app.undisclosed_change_in_circumstance.code}
                    {app.undisclosed_change_in_circumstance.other_description
                      ? ` - ${app.undisclosed_change_in_circumstance.other_description}`
                      : ""}
                  </span>
                )}
                {cicSelectorCDVisible && (
                  <div style={{ marginLeft: "2em", display: "flex" }}>
                    {!app.undisclosed_change_in_circumstance && (
                      <CICSelector
                        showOtherCICInput={showOtherCICInput}
                        selectedCIC={selectedCIC}
                        updateSelectedCIC={updateSelectedCIC}
                        updateShowOtherCICInput={updateShowOtherCICInput}
                        updateOtherCICDescription={updateOtherCICDescription}
                        otherCICDescription={otherCICDescription}
                      />
                    )}
                    <div style={{ marginLeft: "2em" }}>
                      <CICButton
                        updateCICLoading={updateCICLoading}
                        selectedCIC={selectedCIC}
                        otherCICDescription={otherCICDescription}
                        appId={app.id}
                        locked={selectedCIC !== ""}
                        updateShowCICDeletionModal={updateShowCICDeletionModal}
                        notUndisclosedChangeInCircumstance={!app.undisclosed_change_in_circumstance}
                        selectedDisclosure={selectedDisclosure}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          {cicSelectorVisible && (
            <>
              {!app.undisclosed_change_in_circumstance ? (
                <CICSelector
                  showOtherCICInput={showOtherCICInput}
                  selectedCIC={selectedCIC}
                  updateSelectedCIC={updateSelectedCIC}
                  updateShowOtherCICInput={updateShowOtherCICInput}
                  updateOtherCICDescription={updateOtherCICDescription}
                  otherCICDescription={otherCICDescription}
                />
              ) : (
                <p />
              )}

              <div style={{ display: "flex", minWidth: "125px" }}>
                {!CICLoading ? (
                  <CICButton
                    updateCICLoading={updateCICLoading}
                    selectedCIC={selectedCIC}
                    otherCICDescription={otherCICDescription}
                    appId={app.id}
                    locked={locked}
                    notUndisclosedChangeInCircumstance={!app.undisclosed_change_in_circumstance}
                    updateShowCICDeletionModal={updateShowCICDeletionModal}
                    selectedDisclosure={selectedDisclosure}
                  />
                ) : (
                  <InlineLoading status="active" />
                )}
              </div>
            </>
          ) /* end cicSelectorVisible */}
        </div>
        <div className={feeStyles.feeTablesContainer}>
          {fees &&
            groupedFees &&
            prepaids &&
            escrows &&
            feesTableSectionLabels(selectedDisclosure).map(section => {
              // If the row is meant to show an aggregate total, display a "TotalRow" instead
              if (section.isTotalRow) {
                return renderTotalRow(section);
              }

              const sectionFees: DisclosureFee[] = groupedFees[section.letter];
              if (!sectionFees) {
                return renderEmptyFeesTable(section);
              }

              // Otherwise, render standard table for displaying fees
              return renderFeesTable(section, sectionFees);
            })}
        </div>
      </div>
    </>
  );
};

export default FeeTable;
